'use client';

import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef } from 'react';
import { appendBucketCloseToCache, chartHistoryKeys } from '@/lib/api/chart-history/cache';
import { WS_ENDPOINTS } from '@/lib/config/websocket';
import { useWebSocket } from '@/lib/hooks/useWebSocket';
import { useOrderBooksServices } from '@/lib/providers/OrderBookStoreProvider';
import { type BucketSubscription, useChartsStore } from '@/lib/store/charts-store';
import { useOrderFormStore } from '@/lib/store/order-form-store';
import { useOrderbookDeltasStore } from '@/lib/store/orderbook-deltas-store';
import { usePricesStoreBase } from '@/lib/store/prices-store';
import { mergeBucketSubscriptions } from '@/lib/store/utils/charts';
import type {
  BucketSubscriptionMessage,
  OrderBookDataDeltaMessage,
  OrderBookDataMessage,
  PricesDataDeltaMessage,
} from '@/lib/types/websocket';

type OrderBookSocketProviderProps = {
  children: ReactNode;
  eventId: string;
  skipInitialSnapshot?: boolean;
  /**
   * Ended event: no order-book or prices socket. The book is empty and hidden,
   * and its nulls would zero the closing prices in the store.
   */
  isEventEnded?: boolean;
};

export function OrderBookSocketProvider({
  children,
  eventId,
  skipInitialSnapshot = false,
  isEventEnded = false,
}: OrderBookSocketProviderProps) {
  const queryClient = useQueryClient();
  const services = useOrderBooksServices();

  const selectedOutcomeId = useOrderFormStore.use.selectedOutcomeId();
  const selectedOutcomeIdRef = useRef<string | null>(null);
  selectedOutcomeIdRef.current = selectedOutcomeId ?? null;

  const prevEventIdRef = useRef<string | undefined>(undefined);
  const chartsOpenedRef = useRef(false);

  // Skip only the FIRST ORDER_BOOK_DATA after connect when SSR already hydrated.
  // Subsequent snapshots (on outcome switch, on reconnect) must always apply.
  const initialSnapshotConsumedRef = useRef(false);

  // ── Order Book socket ──────────────────────────────────────────────
  const { send } = useWebSocket({
    url: WS_ENDPOINTS.orderBook,
    autoConnect: !isEventEnded,
    onOpen: () => {
      send({ t: 'SUBSCRIBE', o: selectedOutcomeId! });
    },
    onOrderBookData: (msg) => {
      if (msg.t === 'PRICES_DATA') return;

      if (msg.t === 'ORDER_BOOK_DATA') {
        try {
          const deltaMsg = msg as OrderBookDataMessage;
          const outcomeId = deltaMsg.o;
          if (!outcomeId) return;

          const service = services[outcomeId];
          if (!service) return;

          if (skipInitialSnapshot && !initialSnapshotConsumedRef.current) {
            initialSnapshotConsumedRef.current = true;
            return;
          }

          const snapshotPayload = deltaMsg.d;
          if (!snapshotPayload || typeof snapshotPayload !== 'object') return;
          service.initializeFromSnapshot(snapshotPayload);
        } catch (error) {
          console.error('[OrderBookSocketProvider] Error handling ORDER_BOOK_DATA:', error);
        }
      }
    },
    onMessage: (msg) => {
      if (msg.t !== 'ORDER_BOOK_DATA_DELTA') return;

      try {
        const deltaMsg = msg as OrderBookDataDeltaMessage;
        const outcomeId = deltaMsg.o;
        if (!outcomeId) return;

        const service = services[outcomeId];
        if (!service) return;

        const deltaPayload = deltaMsg.d;
        if (!deltaPayload || typeof deltaPayload !== 'object') return;

        service.applyOrderBookDelta(deltaPayload);
        useOrderbookDeltasStore.getState().pushDelta(deltaPayload);
      } catch (error) {
        console.error('[OrderBookSocketProvider] Error handling ORDER_BOOK_DATA_DELTA:', error);
      }
    },
  });

  // ── Prices socket ──────────────────────────────────────────────────
  const { send: sendPrices } = useWebSocket({
    url: WS_ENDPOINTS.orderBookBestBidAsk,
    autoConnect: !isEventEnded,
    onOpen: () => {
      sendPrices({ t: 'SUBSCRIBE', e: eventId });
    },
    onOrderBookData: (msg) => {
      if (msg.t === 'PRICES_DATA') {
        const items = Array.isArray(msg.d) ? msg.d : msg.d ? [msg.d] : [];
        usePricesStoreBase.getState().upsertMany(items);
      } else if (msg.t === 'PRICES_DATA_DELTA') {
        const deltaMsg = msg as PricesDataDeltaMessage;
        if (deltaMsg.d) {
          usePricesStoreBase.getState().upsertMany([deltaMsg.d]);
        }
      }
    },
  });

  // ── Charts socket (bucket closes only, no snapshot) ────────────────
  const bucketSubscriptions = useChartsStore.use.bucketSubscriptions();
  const sentBucketsRef = useRef<BucketSubscription[]>([]);

  const { send: sendCharts } = useWebSocket({
    url: WS_ENDPOINTS.chanceHistory,
    autoConnect: true,
    onOpen: () => {
      // The server forgets subscriptions with the connection: send every current one.
      sendBuckets('SUBSCRIBE_BUCKETS', sentBucketsRef.current);
      // On a reconnect, refetch the history once to fill the closes missed while it was down.
      if (chartsOpenedRef.current && eventId) {
        queryClient.invalidateQueries({ queryKey: chartHistoryKeys.event(eventId) });
      }
      chartsOpenedRef.current = true;
    },
    onMessage: () => {},
    onChartsData: (msg) => {
      // History comes from REST (React Query); the socket appends the bucket closes as-is.
      if (!eventId) return;
      for (const outcomeId of msg.o ?? []) {
        appendBucketCloseToCache(queryClient, eventId, outcomeId, msg.s, msg.d);
      }
    },
  });

  const sendBuckets = (t: BucketSubscriptionMessage['t'], subscriptions: BucketSubscription[]) => {
    for (const { eventId: e, outcomeIds: o, bucketSeconds: s } of subscriptions) {
      sendCharts({ t, e, o, s });
    }
  };

  // Re-subscribe the mounted charts of this event whenever what they draw changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sendBuckets is a plain sender
  useEffect(() => {
    const next = mergeBucketSubscriptions(
      Object.values(bucketSubscriptions).filter((sub) => sub.eventId === eventId),
    );
    sendBuckets('UNSUBSCRIBE_BUCKETS', sentBucketsRef.current);
    sendBuckets('SUBSCRIBE_BUCKETS', next);
    sentBucketsRef.current = next;
  }, [bucketSubscriptions, eventId]);

  // Clear store when navigating to a different event
  useEffect(() => {
    if (prevEventIdRef.current !== undefined && prevEventIdRef.current !== eventId) {
      useChartsStore.getState().clear();
    }
    prevEventIdRef.current = eventId;
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;
    sendPrices({ t: 'SUBSCRIBE', e: eventId });
  }, [eventId, sendPrices]);

  useEffect(() => {
    if (!selectedOutcomeId) return;
    useOrderbookDeltasStore.getState().clear();
    send({ t: 'SUBSCRIBE', o: selectedOutcomeId! });
  }, [selectedOutcomeId, send]);

  return children;
}
